import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  enviarFotoDoVeiculo,
  escolherFotoDoVeiculo,
  fetchFotosDoVeiculo,
  FotoDeEntrada,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * A FOTO DO VEÍCULO da cotação (portal-cotacoes/at-16).
 *
 * Decisão do Victor (28/10/2026): o comprador escolhe uma foto do checklist de entrada, uma vez
 * por cotação. Ela aparece na prévia do link no WhatsApp e no topo da tela de resposta do
 * fornecedor, para ele conferir de que carro é a peça.
 *
 * 🔴 Três estados que não podem se parecer (at-60): a requisição não está ligada a um atendimento
 * da oficina; o atendimento não tem foto no checklist; e a consulta ao SOAC falhou. Os dois
 * primeiros são fatos; o terceiro diz que não deu para verificar.
 */

/** O que a página precisa saber da foto, para avisar antes de enviar os convites sem ela. */
export interface EstadoDaFotoDoVeiculo {
  carregando: boolean;
  erro: boolean;
  /** Há foto no checklist que poderia ser escolhida. */
  haFotosParaEscolher: boolean;
  escolhida: boolean;
}

interface FotoVeiculoSectionProps {
  cotacaoId: string;
  onEstado?: (estado: EstadoDaFotoDoVeiculo) => void;
  /** Cada incremento abre a grade de fotos e rola a tela até o card (atalho do modal de envio). */
  pedidoDeEscolha?: number;
  /** Troca só enquanto a cotação recebe respostas; depois disso o fornecedor não abre mais o link. */
  podeEditar: boolean;
}

const FotoVeiculoSection: React.FC<FotoVeiculoSectionProps> = ({ cotacaoId, podeEditar, onEstado, pedidoDeEscolha }) => {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [disponivel, setDisponivel] = useState(true);
  const [fotos, setFotos] = useState<FotoDeEntrada[]>([]);
  const [atual, setAtual] = useState<string | null>(null);
  const [escolhendo, setEscolhendo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // A rota devolve só a URL da foto escolhida, não o id da mídia; a marcação na grade vale para
  // a escolha feita nesta sessão da tela.
  const [escolhidaId, setEscolhidaId] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pedidoDeEscolha) return;
    if (podeEditar && fotos.length > 0) setEscolhendo(true);
    cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    // Só o pedido dispara: fotos e permissão mudando não podem reabrir a grade sozinhos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoDeEscolha]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(false);
    try {
      const r = await fetchFotosDoVeiculo(cotacaoId);
      setDisponivel(r.disponivel);
      setFotos(r.fotos);
      setAtual(r.atual);
    } catch (error) {
      setErro(true);
      notify.errorFrom(error, 'Não foi possível carregar as fotos do veículo. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setCarregando(false);
    }
  }, [cotacaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    onEstado?.({ carregando, erro, haFotosParaEscolher: disponivel && fotos.length > 0, escolhida: Boolean(atual) });
  }, [onEstado, carregando, erro, disponivel, fotos.length, atual]);

  /**
   * Foto enviada do computador (at-17): o veículo em oficina externa não tem checklist no SOAC, e a
   * foto do dano nem sempre está nele (Alessandra e Phelipe, teste de 29/10/2026).
   */
  async function enviarDoComputador(arquivo: File | undefined) {
    if (!arquivo) return;
    setSalvando(true);
    try {
      const r = await enviarFotoDoVeiculo(cotacaoId, arquivo);
      setAtual(r.atual);
      setEscolhidaId(null);
      setEscolhendo(false);
      notify.success('Foto do veículo enviada.');
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível enviar a foto do veículo. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  }

  async function escolher(midiaId: string | null) {
    setSalvando(true);
    try {
      const r = await escolherFotoDoVeiculo(cotacaoId, midiaId);
      setAtual(r.atual);
      setEscolhidaId(midiaId);
      setEscolhendo(false);
      notify.success(midiaId ? 'Foto do veículo escolhida.' : 'Foto do veículo retirada.');
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível salvar a foto do veículo. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="pc-card" ref={cardRef}>
      <h2 className="pc-card-titulo">Foto do veículo</h2>
      <div className="pc-card-corpo">
        <p className="pc-hint" style={{ marginTop: 0 }}>
          A foto aparece na prévia do link no WhatsApp e no topo da tela do fornecedor, junto com a placa. Escolha uma
          do checklist de entrada do veículo na oficina, ou envie uma do computador (veículo em oficina externa não
          tem checklist).
        </p>

        {carregando ? (
          <p className="pc-texto-secundario">Carregando...</p>
        ) : erro ? (
          <p className="erro-de-tela">
            Não foi possível consultar as fotos do checklist. A cotação pode ter foto escolhida que não está aparecendo
            aqui.
          </p>
        ) : (
          <>
            <div className={`pc-foto-veiculo ${atual ? '' : 'pc-foto-veiculo--vazia'}`}>
              {atual ? (
                <img className="pc-foto-veiculo-imagem" src={atual} alt="Foto do veículo escolhida" />
              ) : (
                <div className="pc-foto-veiculo-sem-foto">Nenhuma foto escolhida</div>
              )}
              <div className="pc-foto-veiculo-info">
                <p className="pc-foto-veiculo-situacao">
                  {atual ? 'Esta é a foto que o fornecedor vê.' : 'O link sai só com a placa.'}
                </p>
                {!disponivel ? (
                  <p className="pc-hint" style={{ margin: 0 }}>
                    Esta requisição não está ligada a um atendimento da oficina, então não há checklist de onde tirar a
                    foto. Envie uma do computador.
                  </p>
                ) : fotos.length === 0 ? (
                  <p className="pc-hint" style={{ margin: 0 }}>
                    O checklist de entrada deste veículo não tem fotos.
                  </p>
                ) : (
                  <p className="pc-hint" style={{ margin: 0 }}>
                    {fotos.length} foto{fotos.length === 1 ? '' : 's'} no checklist de entrada.
                  </p>
                )}
                {podeEditar && (
                  <div className="pc-foto-veiculo-acoes">
                    {disponivel && fotos.length > 0 && (
                      <button
                        type="button"
                        className={`pc-btn pc-btn--sm ${atual ? 'pc-btn--secundario' : 'pc-btn--primario'}`}
                        onClick={() => setEscolhendo((v) => !v)}
                        disabled={salvando}
                      >
                        {escolhendo ? 'Fechar' : atual ? 'Trocar pelo checklist' : 'Escolher do checklist'}
                      </button>
                    )}
                    <label className="pc-btn pc-btn--secundario pc-btn--sm" aria-disabled={salvando}>
                      {salvando ? 'Enviando...' : 'Enviar do computador'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        hidden
                        disabled={salvando}
                        onChange={(e) => {
                          enviarDoComputador(e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {atual && (
                      <button
                        type="button"
                        className="pc-btn pc-btn--secundario pc-btn--sm"
                        onClick={() => escolher(null)}
                        disabled={salvando}
                      >
                        Tirar foto
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {escolhendo && fotos.length > 0 && (
              <>
                <p className="pc-texto-secundario" style={{ marginTop: 14, marginBottom: 0 }}>
                  Clique na foto que vai no link:
                </p>
                <div className="pc-fotos-grade">
                  {fotos.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`pc-foto-opcao ${f.id === escolhidaId ? 'pc-foto-opcao--escolhida' : ''}`}
                      onClick={() => escolher(f.id)}
                      disabled={salvando}
                      title={f.rotulo ?? 'Foto do checklist'}
                    >
                      <img src={f.url} alt={f.rotulo ?? 'Foto do checklist'} />
                      <span className="pc-foto-opcao-rotulo">{f.rotulo ?? 'Foto do checklist'}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FotoVeiculoSection;
